<script lang="ts" setup>
import { graphql } from '@/gql';
import { useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';
import {
    VRow,
    VCol,
    VBtn,
    VCard,
    VSpacer,
    VIcon,
    VList,
    VListItem,
    VListItemTitle,
    VListItemSubtitle,
    VProgressLinear,
    VMenu,
} from 'vuetify/components';
import { useI18n } from 'vue-i18n';
import { PolicyAction } from '@/types';
import { usePermissions } from '@/composables/permissions';
import HighlightDeleteDialog from '@/components/HighlightDeleteDialog.vue';
import HighlightCreateModal from '@/components/HighlightCreateModal.vue';
import HighlightEditModal from '@/components/HighlightEditModal.vue';
import { format, parseISO } from 'date-fns';
import type { Highlight, Project } from '@/gql/graphql';

const { hasProjectPermission } = usePermissions();

const { projectId } = defineProps<{
    projectId: string;
}>();

const { t } = useI18n({
    inheritLocale: true,
    messages: {
        nl: {
            create: '@:highlight aanmaken',
            error: 'Er ging iets mis bij het laden van de @.lower:highlights',
            no_data: 'Geen @.lower:highlights beschikbaar',
        },
        en: {
            create: 'Create @.lower:highlight',
            error: 'Failed to load @.lower:highlights',
            no_data: 'No @.lower:highlights available',
        },
    },
});

const variables = computed(() => ({
    filters: [
        {
            field: 'id',
            value: projectId,
        },
    ],
}));

const { result, loading, error } = useQuery(
    graphql(`
        query ProjectHighlights($filters: [FilterCriterion]) {
            projects(filters: $filters) {
                items {
                    id
                    parentProjectId
                    masterPortfolioId
                    highlights {
                        id
                        name
                        description
                        createdAt
                    }
                }
            }
        }
    `),
    variables,
    { fetchPolicy: 'no-cache' }
);

const project = computed<
    Pick<Project, 'id' | 'parentProjectId' | 'masterPortfolioId' | 'highlights'>
>(() => result.value?.projects?.items[0] || {});
const highlights = computed<
    Pick<Highlight, 'id' | 'name' | 'description' | 'createdAt'>[]
>(() => project?.value?.highlights || []);

const sortedHighlights = computed(() =>
    [...highlights.value].sort((a, b) => {
        const dateA = new Date(a.createdAt as string);
        const dateB = new Date(b.createdAt as string);

        return dateB.getTime() - dateA.getTime();
    })
);
const canUpdateProject = computed(() => {
    if (!project.value) return false;

    return (
        hasProjectPermission(PolicyAction.HIGHLIGHT_WRITE, project.value) ||
        hasProjectPermission(PolicyAction.PROJECT_WRITE, project.value)
    );
});
</script>

<template>
    <v-row>
        <v-col md="6" class="d-flex align-center">
            <h2 class="text-primary-40 text-title-large">
                <VIcon
                    icon="$highlight"
                    color="primary-20"
                    size="1.75rem"
                    class="mr-1"
                />
                {{ t('highlights') }}
            </h2>
        </v-col>

        <v-col md="6" class="d-flex justify-end">
            <v-spacer />
            <HighlightCreateModal
                v-if="canUpdateProject"
                :project-id="String(projectId)"
            >
                <template #activator="{ props }">
                    <v-btn
                        :disabled="loading"
                        v-bind="props"
                        variant="outlined"
                        prepend-icon="$add"
                    >
                        {{ t('create') }}
                    </v-btn>
                </template>
            </HighlightCreateModal>
        </v-col>
    </v-row>
    <v-row>
        <v-col md="12">
            <v-card>
                <v-list
                    v-if="!loading"
                    lines="two"
                    bg-color="transparent"
                    class="py-0"
                >
                    <v-list-item
                        v-for="(highlight, index) in sortedHighlights"
                        :key="highlight.id"
                        :class="{ 'border-t-thin': index > 0 }"
                        class="justify-start"
                    >
                        <v-list-item-title
                            color="primary-95"
                            class="text-primary font-weight-bold overflow-y-visible text-wrap mb-1"
                        >
                            {{ highlight.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            {{ highlight.description }}
                        </v-list-item-subtitle>
                        <template #append>
                            <p class="text-body-small opacity-50 ml-5">
                                {{
                                    format(
                                        parseISO(highlight.createdAt as string),
                                        'dd-MM-yyyy'
                                    )
                                }}
                            </p>
                            <v-menu v-if="canUpdateProject">
                                <template
                                    #activator="{ props: activatorProps }"
                                >
                                    <v-btn
                                        icon="$more"
                                        variant="text"
                                        class="ml-3 px-0"
                                        :style="{ '--v-btn-height': '1rem' }"
                                        v-bind="activatorProps"
                                    ></v-btn>
                                </template>
                                <v-list>
                                    <HighlightEditModal :highlight>
                                        <template #activator="{ props }">
                                            <v-list-item v-bind="props">{{
                                                t('edit')
                                            }}</v-list-item>
                                        </template>
                                    </HighlightEditModal>
                                    <HighlightDeleteDialog
                                        :highlight-id="highlight.id"
                                    >
                                        <template #activator="{ props }">
                                            <v-list-item v-bind="props">{{
                                                t('delete')
                                            }}</v-list-item>
                                        </template>
                                    </HighlightDeleteDialog>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-list-item>
                </v-list>
                <p v-if="!loading && !highlights.length">
                    {{ t('no_data') }}
                </p>
                <p v-if="!loading && error">
                    {{ t('error') }}
                </p>
                <v-progress-linear v-if="loading" indeterminate />
            </v-card>
        </v-col>
    </v-row>
</template>

<style lang="scss" scoped>
.v-list-item-title {
    line-height: 1.1;
}

.v-list-item--two-line {
    :deep(.v-list-item-subtitle) {
        line-clamp: revert;
        -webkit-line-clamp: revert;
    }
}

:deep(.v-list-item__append) {
    align-self: start;
}
</style>
